import styled from 'styled-components';
import { TableEvenOdd } from '../../styles';
import { message, Pagination } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useRef } from 'react';
import { Content } from 'antd/es/layout/layout';
import useGetPackageHistoryTopUp from '../services/hooks/useGetPackageHistoryTopUp';
import { useParams } from 'react-router-dom';
import { IHistoryTopUpContent } from '../services/types';
import Text from '../../../../components/Text';
import Box from '../../../../components/Box';
import Button from '../../../../components/Button';
import StatusDropdownTag from './StatusDropdownTag';
import { AxiosResponse } from 'axios';
import accountApis from '../../../../services/accountApis';
import { getMessageError } from '../../../../utils/response';
import TopUpAmountModal, { TopUpAmountModalRef } from '../TopUpAmountModal';

const ExtendHistoryTable = () => {
  const { shopId } = useParams();
  const { data: historyData, loading: historyLoading, page, setPage, mutate } = useGetPackageHistoryTopUp(shopId || '');
  const useTopUpModalRef = useRef<TopUpAmountModalRef>(null);

  const handleUpdateStatus = async (id: string, newStatus: string) => {
    try {
      const res: AxiosResponse<{ data: any }> = await accountApis.updateStatusTopUp({
        id,
        status: newStatus,
      });

      if (res.data.data) {
        message.success('Status updated successfully');
        mutate({
          page,
          size: 10,
        });
      }
    } catch (error) {
      message.error(getMessageError(error) || 'Failed to update status');
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Date & Time',
      dataIndex: 'createdDate',
      className: 'dateTime',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      className: 'amount',
    },
    {
      title: 'Note',
      dataIndex: 'note',
      className: 'note',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      className: 'type',
    },
    {
      title: 'Processed Date',
      dataIndex: 'approvedDate',
      className: 'processedDate',
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      className: 'status',
      render: (status: string, record: IHistoryTopUpContent) => (
        <StatusDropdownTag
          value={status as any}
          onChange={(newStatus) => {
            handleUpdateStatus(record.id, newStatus);
          }}
        />
      ),
    },
  ];

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    mutate({ page: newPage, size: 10 });
  };

  const handleAddTopUpAmount = () => {
    mutate({ page: 1, size: 10 });
  };

  return (
    <Container>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Text variant='H9' style={{ fontWeight: 600 }}>
          Extend History
        </Text>
        <Button icon='add' variant='SECONDARY' size='small' onClick={() => useTopUpModalRef.current?.open()}>
          Top Up Amount
        </Button>
      </Box>
      <TableContentStyled
        loading={historyLoading}
        columns={columns}
        dataSource={historyData?.content || []}
        rowKey='id'
        pagination={false}
      />

      <PagingStyled>
        <Pagination
          current={page}
          total={historyData?.totalElements}
          pageSize={10}
          onChange={handleChangePage}
          showSizeChanger={false}
        />
      </PagingStyled>
      <TopUpAmountModal ref={useTopUpModalRef} onSuccess={handleAddTopUpAmount} />
    </Container>
  );
};

export default ExtendHistoryTable;

const Container = styled.div`
  flex: 1;
  width: 100%;
  gap: 16px;
  display: flex;
  flex-direction: column;
`;

const TableContentStyled = styled(TableEvenOdd)`
  && .dateTime {
    width: 20%;
  }

  && .amount {
    width: 10%;
  }

  && .note {
    width: 15%;
  }

  && .type {
    width: 10%;
  }

  && .processedDate {
    width: 15%;
  }

  && .status {
    width: 10%;
  }

  && .progress {
    background-color: #eee;
    border-radius: 10px;
    display: inline-flex;
    font-size: 0.75rem;
    height: 20px;
    line-height: 0;
    overflow: hidden;
    position: relative;
    width: 104px;
  }

  && .progress span {
    color: rgba(245, 118, 127, 1);
    font-size: 12px;
    font-weight: 700;
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
    text-align: center;
  }

  && .progress-bar {
    background-color: rgba(255, 229, 231, 1);
    color: #fff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    text-align: center;
    transition: width 0.6s ease;
    white-space: nowrap;
  }

  && .coupon-label-status {
    border-radius: 2px;
    color: #1d2129;
    font-size: 14px;
    font-weight: 500;
    gap: 10px;
    height: 28px;
    padding: 4px 8px;
    width: 60px;
  }

  && .btn-delete,
  .btn-edit {
    background: transparent;
    border: none;
    padding: 8px;
  }

  && .btn-delete:hover {
    background: rgba(245, 118, 127, 1) !important;
    border: none !important;
  }

  && .btn-edit:hover {
    background: var(--blue, #008bff) !important;
    border: none !important;
  }

  thead > tr > th.ant-table-cell {
    padding: 8px 16px;
    p {
      margin: 0;
    }
  }
`;

const PagingStyled = styled(Content)`
  text-align: right;
  margin-top: 10px;
`;
